/* Common Imports */

import { styled } from "@mui/system";
import { createHmac } from "crypto";
import { NextRouter } from "next/router";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";
import {
	UpdateAiOverviewThunk,
	UpdateOverviewThunk,
	changeDataTuningThunk,
	setDataBeforeEditThunk,
	setPropertySnackbarThunk,
	setSectionToEditThunk,
	setSinglePropertyAboutLocationThunk,
	setSinglePropertyAboutThunk,
	setSinglePropertyAgeThunk,
	setSinglePropertyAmenitiesThunk,
	setSinglePropertyAvailabiltyThunk,
	setSinglePropertyCategoryTypeThunk,
	setSinglePropertyDescriptionThunk,
	setSinglePropertyDeveloperNameThunk,
	setSinglePropertyEstablishedYearThunk,
	setSinglePropertyFloorNoThunk,
	setSinglePropertyFurnishingThunk,
	setSinglePropertyLandAreaThunk,
	setSinglePropertyLandFacingThunk,
	setSinglePropertyManualOverviewThunk,
	setSinglePropertyMaxBalconiesThunk,
	setSinglePropertyMaxBathroomsThunk,
	setSinglePropertyMaxBedroomsThunk,
	setSinglePropertyMaxCarpetAreaThunk,
	setSinglePropertyMaxFloorsThunk,
	setSinglePropertyMaxPriceThunk,
	setSinglePropertyMaxSuperBuiltUpAreaThunk,
	setSinglePropertyMinBalconiesThunk,
	setSinglePropertyMinBathroomsThunk,
	setSinglePropertyMinBedroomsThunk,
	setSinglePropertyMinCarpetAreaThunk,
	setSinglePropertyMinPriceThunk,
	setSinglePropertyMinSuperBuiltUpAreaThunk,
	setSinglePropertyNoOfSeatsThunk,
	setSinglePropertyNoOfUnitsThunk,
	setSinglePropertyOtherPlotDimensionsThunk,
	setSinglePropertyOverviewThunk,
	setSinglePropertyPlotDimensionsThunk,
	setSinglePropertyPossessionDateThunk,
	setSinglePropertySecurityDepositThunk,
	setSinglePropertySubTypeThunk,
	setSinglePropertyTagsThunk,
	setSinglePropertyTransactionTypeThunk,
	setSinglePropertyTypeThunk,
	setSinglePropertyUnitFacingThunk,
	setSinglePropertyVicinityThunk,
} from "../../../../../redux-magic/store";

/* Component Imports */

import {
	Alert,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	Paper,
	Skeleton,
	Snackbar,
	Switch,
	Tab,
	Tabs,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { useRouter } from "next/router";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";

/* Icon Imports */

import { AutoAwesomeOutlined, Close, EditOutlined } from "@mui/icons-material";

/* Styled Components */

const PaperContainer = styled(Paper)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	borderRadius: "16px",
	boxShadow: "none",
	background: theme.palette.background.paper,
	padding: "0.875rem",
	[theme.breakpoints.up("sm")]: {
		padding: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		padding: "1.5rem",
	},
}));

const Contain = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	margin: "0rem 0rem 0.25rem 0rem",
	[theme.breakpoints.up("md1190")]: {
		margin: "0rem 0rem 0.5rem 0rem",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const SubHeading = styled(Typography)(({ theme }) => ({
	margin: "0rem 0rem 0rem 0rem",
	width: "100%",
	fontWeight: "400",
	fontSize: "0.825rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
}));

const ShowMore = styled(Typography)(({ theme }) => ({
	margin: "0.25rem 0rem 0rem 0rem",
	width: "100%",
	fontWeight: "500",
	color: theme.palette.primary.main,
	textAlign: "center",
	fontSize: "0.85rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
		margin: "0.5rem 0rem 0rem 0rem",
	},
}));

const Snack = styled(Snackbar)(({ theme }) => ({}));

const ButtonContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
}));

const ButtonDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	gap: "1rem",
}));

const SwitchDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "center",
	gap: "1rem",
	margin: "0.5rem 0rem 0rem 0rem",
}));

const ButtonSwitchDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	[theme.breakpoints.down("sm")]: {
		flexDirection: "column",
	},
}));

const CustomTab = styled(Tab)(({ theme }) => ({
	flexGrow: 1,
	textTransform: "capitalize",
	padding: "0rem",
	[theme.breakpoints.down("sm")]: {
		minWidth: "7.1rem",
	},
}));

const TabTitle = styled(Typography)(({ theme }) => ({
	fontWeight: 500,
	fontSize: "0.875rem",
}));

const IOSSwitch = styled(Switch)(({ theme }) => ({
	width: 40,
	height: 24,
	padding: 0,
	"& .MuiSwitch-switchBase": {
		padding: 0,
		margin: 2,
		transitionDuration: "300ms",
		"&.Mui-checked": {
			transform: "translateX(16px)",
			color: "#fff",
			"& + .MuiSwitch-track": {
				// backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "#65C466",
				opacity: 1,
				border: 0,
			},
			"&.Mui-disabled + .MuiSwitch-track": {
				opacity: 0.5,
			},
		},
		"&.Mui-focusVisible .MuiSwitch-thumb": {
			color: "#33cf4d",
			border: "6px solid #fff",
		},
		"&.Mui-disabled .MuiSwitch-thumb": {
			color: theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[600],
		},
		"&.Mui-disabled + .MuiSwitch-track": {
			opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
		},
	},
	"& .MuiSwitch-thumb": {
		boxSizing: "border-box",
		width: 20,
		height: 20,
	},
	"& .MuiSwitch-track": {
		borderRadius: 26 / 2,
		backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,.35)" : "rgba(0,0,0,.25)",
		opacity: 1,
	},
}));

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

function TabPanel(props: any) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			style={{ width: "100%" }}
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && children}
		</div>
	);
}

function a11yProps(index: any) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	};
}

const OverviewCard = ({
	overview_data,
	search_metrics,
	property_id,
	property_created_by,
	dispatch,
	session,
	negativeScroll,
	isEditor,
	profile_context,
	loading,
	section_to_edit,
	data_before_edit,
	assigned_to,
	overview_snackbar,
	slug,
	aigenerated,
	manual_overview,
	use_data_for_fine_tuning,
}: {
	overview_data: string;
	search_metrics: PropertyInterface["search_metrics"];
	property_id: string | ObjectId;
	property_created_by: LocalPropertyInterface["created_by"];
	dispatch: Function;
	session: any;
	negativeScroll: boolean;
	isEditor: boolean;
	profile_context: StateInterface["profile_context"];
	loading: boolean;
	section_to_edit: string;
	data_before_edit: any;
	assigned_to: StateInterface["single_property_page"]["property"]["assigned_to"];
	overview_snackbar: boolean;
	slug: string;
	aigenerated: boolean;
	manual_overview: string;
	use_data_for_fine_tuning: boolean;
}) => {
	const theme = useTheme();
	/*

  & Let's declare the router. Here we are using the useRouter hook from nextjs.

  & We are using the router to get the query params from the url. Memoize the router object to prevent unnecessary re-renders.

  */

	const router: NextRouter = React.useRef(useRouter()).current;

	const localLang = {
		overview: router.locale === "kn-IN" ? "ಓವರ್‌ವ್ಯೂ" : router.locale === "ar-AE" ? "نظرة عامة" : "Overview",
		showMore: router.locale === "kn-IN" ? "ಮತ್ತಷ್ಟು ತೋರಿಸಿ" : router.locale === "ar-AE" ? "رؤية المزيد" : "See more",
		showLess: router.locale === "kn-IN" ? "ಕಡಿಮೆ ತೋರಿಸಿ" : router.locale === "ar-AE" ? "رؤية أقل" : "See less",
		manualOverview:
			router.locale === "kn-IN" ? "ನೀವು ಬರೆಯಿರಿ" : router.locale === "ar-AE" ? "نظرة عامة يدويّة" : "Manual overview",
		inUse: router.locale === "kn-IN" ? "ಬಳಕೆಯಲ್ಲಿದೆ" : router.locale === "ar-AE" ? "قيد الاستخدام" : "in use",
		generateWithAI:
			router.locale === "kn-IN"
				? "AI ಜೊತೆಗೇ ಉತ್ಪಾದಿಸಿ"
				: router.locale === "ar-AE"
					? "توليد باستخدام الذكاء الاصطناعي"
					: "Generate with AI",
		update: router.locale === "kn-IN" ? "ಅಪ್ಡೇಟ್ ಮಾಡಿ" : router.locale === "ar-AE" ? "تحديث" : "Update",
		useDataForFineTuning:
			router.locale === "kn-IN"
				? "ಫೈನ್ ಟ್ಯೂನಿಂಗ್ ಮಾಡಲು ಡೇಟಾ ಬಳಸಿ"
				: router.locale === "ar-AE"
					? "استخدم البيانات من أجل التعديل الدقيق"
					: "Use data for fine tuning",
		aiOverview:
			router.locale === "kn-IN"
				? "ಏಐ ಓವರ್‌ವ್ಯೂ"
				: router.locale === "ar-AE"
					? "نظرة عامة عن الذكاء الاصطناعي"
					: "AI Overview",
		generating:
			router.locale === "kn-IN" ? "ಜನರೇಟಿಂಗ್..." : router.locale === "ar-AE" ? "جاري التوليد..." : "Generating...",
		snackValue:
			router.locale === "kn-IN"
				? "ಆಸ್ತಿ ಅವಲೋಕನವನ್ನು ಯಶಸ್ವಿಯಾಗಿ ನವೀಕರಿಸಲಾಗಿದೆ"
				: router.locale === "ar-AE"
					? "تم تحديث نظرة العقار العامة بنجاح"
					: "Property overview updated successfully",
	};

	const [showMore, setShowMore] = React.useState<Boolean>(false);

	const [load, setLoad] = React.useState<boolean>(false);

	const [aiload, setAiLoad] = React.useState<boolean>(false);

	const [removeaibutton, setRemoveAiButton] = React.useState<boolean>(true);

	const [isButtonDisabled, setIsButtonDisabled] = React.useState<boolean>(true);

	const [isEditing, setIsEditing] = React.useState<boolean>(false);

	const [value, setValue] = React.useState(0);

	const [aigeneratedOverview, setAiGeneratedOverview] = React.useState<string>("");

	const [isAiOverviewEnabled, setIsAiOverviewEnabled] = React.useState<boolean>(false);
	const [isManualOverviewTabDisabled, setIsManualOverviewTabDisabled] = React.useState<boolean>(false);

	const [isTextFieldDisabled, setIsTextFieldDisabled] = React.useState(false);

	const [buttonText, setButtonText] = React.useState(localLang.generateWithAI);

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	};
	const [spinner, setSpinner] = React.useState<boolean>(false);
	const handleChangeTuning = async (event: React.ChangeEvent<HTMLInputElement>) => {
		setSpinner(true);
		await dispatch(
			changeDataTuningThunk({
				property_id: property_id,
				fine_tuning_check: !use_data_for_fine_tuning,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
			}),
		);
		setSpinner(false);
	};

	const profile_context_user = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: null;

	React.useEffect(() => {
		if (section_to_edit !== "property-overview") {
			setIsEditing(false);
		}
	}, [data_before_edit, section_to_edit]);

	const newDelimitedSearchString = search_metrics ? search_metrics.search_string_delimiter.split("ߐ") : [];
	newDelimitedSearchString[6] = overview_data;
	React.useEffect(() => {
		if (aigenerated) {
			setAiGeneratedOverview(overview_data);
			setIsAiOverviewEnabled(true);
			setValue(1);
		} else {
			setIsAiOverviewEnabled(false);
		}
	}, [aigenerated, overview_data]);

	if (loading) {
		return (
			<PaperContainer
				id="overview"
				sx={{ scrollMarginTop: negativeScroll === true ? "4rem" : "8rem" }}
			>
				<Contain>
					<Heading>
						<Skeleton
							animation="wave"
							variant="text"
							width="15rem"
						/>
					</Heading>
				</Contain>
				<Skeleton
					animation="wave"
					variant="text"
					width="100%"
				/>
				<Skeleton
					animation="wave"
					variant="text"
					width="100%"
				/>
				<Skeleton
					animation="wave"
					variant="text"
					width="80%"
				/>
			</PaperContainer>
		);
	}

	// Generate HMAC
	const generatedsign = createHmac("sha256", process.env.GENERATE_OVERVIEW_SECRET_KEY || "")
		.update(slug)
		.digest("hex");

	const submitGenerateAI = async () => {
		setIsManualOverviewTabDisabled(true);
		setIsAiOverviewEnabled(true);
		setButtonText(localLang.generating);
		setValue(1);
		setAiLoad(true);
		setIsButtonDisabled(true);
		const summary = await fetch(`https://summarize.beegru.com`, {
			method: "POST",
			headers: {
				"Content-Type": "text/plain",
				"x-summary-signature": generatedsign,
			},
			body: JSON.stringify({
				slug: slug,
			}),
		});
		const data = await summary.json();
		setAiGeneratedOverview(data.summary);
		setAiLoad(false);
		setIsButtonDisabled(false);
		setRemoveAiButton(false);
		setIsManualOverviewTabDisabled(false);
		setButtonText(localLang.generateWithAI);
	};

	const submitAi = async () => {
		await dispatch(
			UpdateAiOverviewThunk({
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				overview: aigeneratedOverview,
				delimited_search_string: newDelimitedSearchString.join("ߐ"),
				search_string: newDelimitedSearchString.join(""),
				property_id: property_id,
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: property_created_by.is_business_profile,
				editor_user_id: isEditor ? profile_context.user_id : (property_created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (property_created_by.business_profile_id as BusinessProfileInterface)._id,
				url: router.asPath ? router.asPath : "",
				manual_overview: overview_data,
			}),
		);
		setAiLoad(false);
		setIsButtonDisabled(true);
		setIsEditing(false);
		overviewContainer
			? overviewContainer.scrollIntoView({
					behavior: "smooth",
					block: "center",
					inline: "start",
				})
			: null;
		setLoad(false);
	};

	const overviewContainer: HTMLElement | null = document.getElementById("overview");

	const submitForm = async () => {
		setLoad(true);
		await dispatch(
			UpdateOverviewThunk({
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				overview: overview_data,
				manual_overview: manual_overview,
				delimited_search_string: newDelimitedSearchString.join("ߐ"),
				search_string: newDelimitedSearchString.join(""),
				property_id: property_id,
				editor_is_business_profile: isEditor
					? profile_context.is_business_profile
					: property_created_by.is_business_profile,
				editor_user_id: isEditor
					? profile_context.user_id
					: property_created_by.is_business_profile
						? (property_created_by.user_id as UserInterface)._id
						: (property_created_by.user_id as UserInterface)._id,
				editor_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (property_created_by.business_profile_id as BusinessProfileInterface)._id,
				url: router.asPath ? router.asPath : "",
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
			}),
		);
		dispatch(setSectionToEditThunk(""));
		dispatch(setDataBeforeEditThunk({}));
		setIsEditing(false);
		overviewContainer
			? overviewContainer.scrollIntoView({
					behavior: "smooth",
					block: "center",
					inline: "start",
				})
			: null;
		setLoad(false);
	};

	const logged_user: ObjectId | string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser._id
					? session.user.dbUser._id
					: ""
				: ""
			: ""
		: "";

	const logged_user_access: string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser
					? session.user.dbUser.access_level
						? session.user.dbUser.access_level
						: ""
					: ""
				: ""
			: ""
		: "";

	const creator_user_id: string | ObjectId | UserInterface | BusinessProfileInterface = property_created_by
		? property_created_by.is_business_profile
			? property_created_by.business_profile_id != ""
				? (property_created_by.business_profile_id as BusinessProfileInterface)._id
				: (property_created_by.business_profile_id as string)
			: property_created_by.user_id != ""
				? (property_created_by.user_id as UserInterface)._id
				: (property_created_by.user_id as string)
		: "";

	const is_user_creator: boolean =
		profile_context_user !== ""
			? creator_user_id !== ""
				? profile_context_user === creator_user_id
					? true
					: false
				: false
			: false;

	return (
		<PaperContainer
			id="overview"
			sx={{ scrollMarginTop: negativeScroll === true ? "4rem" : "8rem" }}
		>
			<Snack
				open={overview_snackbar}
				autoHideDuration={3000}
				onClose={() => dispatch(setPropertySnackbarThunk(false))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => dispatch(setPropertySnackbarThunk(false))}
					severity="success"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
					variant="filled"
				>
					{localLang.snackValue}
				</Alert>
			</Snack>

			{/* Icons */}

			<Contain>
				<Heading>{localLang.overview}</Heading>

				{is_user_creator ||
				(logged_user_access.includes("admin") &&
					logged_user_access.includes("property") &&
					assigned_to.includes(logged_user)) ||
				logged_user_access.includes("super_admin") ||
				isEditor ? (
					<React.Fragment>
						{!isEditing ? (
							<IconButton
								id="spp_overview_edit_button"
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={() => {
									dispatch(setSectionToEditThunk("property-overview"));
									if (section_to_edit !== "property-overview") {
										switch (section_to_edit) {
											case "property-details":
												dispatch(setSinglePropertyCategoryTypeThunk(data_before_edit.property_category_type));
												dispatch(setSinglePropertyTransactionTypeThunk(data_before_edit.property_transaction_type));
												dispatch(setSinglePropertyTypeThunk(data_before_edit.property_type));
												dispatch(setSinglePropertySubTypeThunk(data_before_edit.property_subtype));
												dispatch(setSinglePropertyMinPriceThunk(data_before_edit.property_min_price));
												dispatch(setSinglePropertyMaxPriceThunk(data_before_edit.property_max_price));
												dispatch(setSinglePropertySecurityDepositThunk(data_before_edit.property_security_deposit));
												dispatch(setSinglePropertyAvailabiltyThunk(data_before_edit.property_availability));
												dispatch(setSinglePropertyPossessionDateThunk(data_before_edit.property_possession_date));
												dispatch(setSinglePropertyTagsThunk(data_before_edit.property_tags));
												dispatch(setSinglePropertyUnitFacingThunk(data_before_edit.property_unit_facing));
												dispatch(setSinglePropertyLandFacingThunk(data_before_edit.property_land_facing));
												dispatch(setSinglePropertyAgeThunk(data_before_edit.property_age));
												break;
											case "property-config":
												dispatch(setSinglePropertyFurnishingThunk(data_before_edit.property_furnishing_status));
												dispatch(setSinglePropertyLandAreaThunk(data_before_edit.property_area));
												dispatch(setSinglePropertyMaxBalconiesThunk(data_before_edit.property_balconies_max));
												dispatch(setSinglePropertyMaxBathroomsThunk(data_before_edit.property_bathroom_max));
												dispatch(setSinglePropertyMaxBedroomsThunk(data_before_edit.property_bhk_max));
												dispatch(setSinglePropertyMaxCarpetAreaThunk(data_before_edit.property_carpet_area_max));
												dispatch(setSinglePropertyMaxFloorsThunk(data_before_edit.property_floors_max));
												dispatch(setSinglePropertyMaxSuperBuiltUpAreaThunk(data_before_edit.super_builtup_max));
												dispatch(setSinglePropertyMinBalconiesThunk(data_before_edit.property_balconies_min));
												dispatch(setSinglePropertyMinBathroomsThunk(data_before_edit.property_bathroom_min));
												dispatch(setSinglePropertyMinBedroomsThunk(data_before_edit.property_bedroom_min));
												dispatch(setSinglePropertyMinCarpetAreaThunk(data_before_edit.property_carpet_area_mix));
												dispatch(setSinglePropertyMinSuperBuiltUpAreaThunk(data_before_edit.super_builtup_min));
												dispatch(setSinglePropertyNoOfUnitsThunk(data_before_edit.property_no_of_units));
												dispatch(setSinglePropertyNoOfSeatsThunk(data_before_edit.property_no_of_seats));
												dispatch(setSinglePropertyFloorNoThunk(data_before_edit.property_floor_no));
												dispatch(setSinglePropertyOtherPlotDimensionsThunk(data_before_edit.other_plot_dimensions));
												dispatch(setSinglePropertyPlotDimensionsThunk(data_before_edit.property_plot_dimensions));
												break;
											case "about-property":
												dispatch(setSinglePropertyAboutThunk(data_before_edit));
												break;
											case "property-amenities":
												dispatch(setSinglePropertyAmenitiesThunk(data_before_edit));
												break;
											case "property-developer":
												dispatch(setSinglePropertyDeveloperNameThunk(data_before_edit.name));
												dispatch(setSinglePropertyDescriptionThunk(data_before_edit.description));
												dispatch(setSinglePropertyEstablishedYearThunk(data_before_edit.year_est));
												break;
											case "property-location":
												dispatch(setSinglePropertyAboutLocationThunk(data_before_edit));
												break;
											case "property-vicinity":
												dispatch(setSinglePropertyVicinityThunk(data_before_edit));
												break;
											default:
												break;
										}
										dispatch(setSectionToEditThunk("property-overview"));
									}
									setIsEditing(true);
									dispatch(setDataBeforeEditThunk(overview_data));
								}}
							>
								<EditOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						) : (
							<IconButton
								id="spp_overview_close_button"
								color="primary"
								sx={{
									padding: "0.25rem",
								}}
								onClick={() => {
									setIsEditing(false);
									dispatch(setSinglePropertyOverviewThunk(aigenerated ? overview_data : data_before_edit));
								}}
							>
								<Close
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						)}
					</React.Fragment>
				) : null}
			</Contain>

			{!isEditing ? (
				/* User View */

				<React.Fragment>
					<SubHeading variant="body1">
						{overview_data.length > 200
							? showMore
								? overview_data
								: overview_data.substring(0, 200) + "..."
							: overview_data}
					</SubHeading>

					<div
						style={{
							color: "transparent",
							width: "100%",
						}}
					>
						{overview_data.length > 200 && (
							<ShowMore
								onClick={() => setShowMore(!showMore)}
								sx={{ cursor: "pointer" }}
							>
								{showMore ? localLang.showLess : localLang.showMore}
							</ShowMore>
						)}
					</div>
				</React.Fragment>
			) : (
				/* Edit View */

				<div style={{ width: "100%" }}>
					<Tabs
						value={value}
						onChange={handleChange}
						variant="scrollable"
						scrollButtons={true}
						allowScrollButtonsMobile
					>
						<CustomTab
							label={
								<div
									style={{
										display: "flex",
										flexDirection: "row",
										gap: "0.5rem",
									}}
								>
									<TabTitle sx={{ display: "flex", alignItems: "center" }}>{localLang.manualOverview}</TabTitle>
									{!aigenerated ? (
										<Chip
											sx={{ border: "1px solid #1BA672", lineHeight: "1.5rem", color: "#1BA672" }}
											label="in use"
											variant="outlined"
										/>
									) : null}
								</div>
							}
							{...a11yProps(aigenerated ? 1 : 0)}
							disabled={isManualOverviewTabDisabled}
						/>
						{isAiOverviewEnabled && (
							<CustomTab
								icon={<AutoAwesomeOutlined />}
								iconPosition="start"
								aria-label="auto awesome"
								label={
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											gap: "0.5rem",
										}}
									>
										<Typography
											sx={{
												display: "flex",
												alignItems: "center",
												fontWeight: 500,
												fontSize: "0.875rem",
												background: "linear-gradient(90deg, #DE6571 0%, #647FE9 100%)",
												WebkitBackgroundClip: "text",
												WebkitTextFillColor: "transparent",
											}}
										>
											{localLang.aiOverview}
										</Typography>
										{aigenerated ? (
											<Chip
												sx={{ border: "1px solid #1BA672", lineHeight: "1.5rem", color: "#1BA672" }}
												label="in use"
												variant="outlined"
											/>
										) : null}
									</div>
								}
								{...a11yProps(aigenerated ? 0 : 1)}
							/>
						)}
					</Tabs>

					<TabPanel
						value={value}
						index={0}
						sx={{ width: "100%" }}
					>
						{aigenerated ? (
							<TextField
								id="spp_overview_manual_texfield"
								value={manual_overview}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									if (section_to_edit !== "property-overview") {
										dispatch(setSectionToEditThunk("property-overview"));
									}
									setIsButtonDisabled(false);
									dispatch(setSinglePropertyManualOverviewThunk(event.target.value));
								}}
								multiline
								maxRows={10}
								// label="Overview"
								placeholder="Eg: RERA Registered, BBMP Approved, IGBC Certified, Vaastu Compliance etc."
								fullWidth
								autoComplete="off"
								sx={{
									width: "100%",
									"& .MuiInputBase-input": {
										fontSize: "0.825rem",
										[theme.breakpoints.up("sm")]: {
											fontSize: "0.875rem",
										},
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1rem",
										},
									},
								}}
							/>
						) : (
							<TextField
								id="spp_ai_overview_textfield"
								value={overview_data}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
									if (section_to_edit !== "property-overview") {
										dispatch(setSectionToEditThunk("property-overview"));
									}
									setIsButtonDisabled(false);
									dispatch(setSinglePropertyOverviewThunk(event.target.value));
								}}
								multiline
								maxRows={10}
								// label="Overview"
								placeholder="Eg: RERA Registered, BBMP Approved, IGBC Certified, Vaastu Compliance etc."
								fullWidth
								autoComplete="off"
								sx={{
									width: "100%",
									"& .MuiInputBase-input": {
										fontSize: "0.825rem",
										[theme.breakpoints.up("sm")]: {
											fontSize: "0.875rem",
										},
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1rem",
										},
									},
								}}
							/>
						)}
						<ButtonSwitchDiv>
							<ButtonDiv>
								{(logged_user_access.includes("admin") && logged_user_access.includes("property")) ||
								logged_user_access.includes("super_admin") ? (
									<Button
										id="spp_generate_ai_overview_button"
										sx={{
											marginTop: "1.125rem",
											background: "linear-gradient(90deg, #DE6571 0%, #647FE9 100%)",
											boxShadow: "none",
											borderRadius: "0.5rem",
											textTransform: "capitalize",
										}}
										variant="contained"
										type="button"
										name="generate ai"
										startIcon={<AutoAwesomeOutlined />}
										onClick={submitGenerateAI}
									>
										{buttonText}
									</Button>
								) : null}

								<BeegruButton
									id="spp_update_overview_button"
									flavor="primary"
									onClick={submitForm}
									sx={{ marginTop: "1.125rem" }}
									variant="contained"
									type="button"
									name="Update"
									disabled={isButtonDisabled}
								>
									{localLang.update}
								</BeegruButton>
							</ButtonDiv>
							{(logged_user_access.includes("admin") &&
								logged_user_access.includes("property") &&
								assigned_to.includes(logged_user)) ||
							logged_user_access.includes("super_admin") ||
							isEditor ? (
								spinner ? (
									<CircularProgress
										// size={24}
										sx={{
											marginTop: "1rem",
										}}
									/>
								) : (
									<SwitchDiv>
										<SubHeading>{localLang.useDataForFineTuning}</SubHeading>
										<IOSSwitch
											checked={use_data_for_fine_tuning}
											onChange={handleChangeTuning}
										/>
									</SwitchDiv>
								)
							) : null}
						</ButtonSwitchDiv>
					</TabPanel>
					<TabPanel
						value={value}
						index={1}
					>
						<TextField
							id="spp_ai_overview_textfield"
							value={aigeneratedOverview}
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								if (section_to_edit !== "property-overview") {
									dispatch(setSectionToEditThunk("property-overview"));
								}
								setIsButtonDisabled(false);
								setAiGeneratedOverview(event.target.value);
								// if (aigenerated) {
								// 	dispatch(setSinglePropertyOverviewThunk(event.target.value));
								// }
							}}
							multiline
							rows={6}
							placeholder="Eg: RERA Registered, BBMP Approved, IGBC Certified, Vaastu Compliance etc."
							fullWidth
							autoComplete="off"
							sx={{ width: "100%" }}
						/>
						{!removeaibutton ? (
							<Typography
								variant="body2"
								sx={{
									display: "flex",
									justifyContent: "flex-end",
									alignItems: "center",
									marginTop: "0.25rem",
									color: theme.palette.mode == "dark" ? "#FFFFFF" : "#666666",
								}}
							>
								{localLang.generateWithAI}
							</Typography>
						) : null}
						<ButtonContainer>
							<ButtonDiv>
								{isEditing ? (
									<>
										{(logged_user_access.includes("admin") && logged_user_access.includes("property")) ||
										logged_user_access.includes("super_admin") ? (
											<Button
												id="spp_generate_ai_overview_button"
												sx={{
													marginTop: "1.125rem",
													background: "linear-gradient(90deg, #DE6571 0%, #647FE9 100%)",
													boxShadow: "none",
													borderRadius: "0.5rem",
													textTransform: "capitalize",
												}}
												variant="contained"
												type="button"
												name="generate ai"
												startIcon={<AutoAwesomeOutlined />}
												onClick={submitGenerateAI}
												disabled={isButtonDisabled}
											>
												{buttonText}
											</Button>
										) : null}

										{load ? (
											<CircularProgress
												// size={24}
												sx={{
													marginTop: "1rem",
												}}
											/>
										) : (
											<BeegruButton
												id="spp_update_overview_button"
												flavor="primary"
												onClick={submitAi}
												sx={{ marginTop: "1.125rem" }}
												variant="contained"
												type="button"
												name="Update"
												disabled={isButtonDisabled}
											>
												{localLang.update}
											</BeegruButton>
										)}
									</>
								) : null}
							</ButtonDiv>
						</ButtonContainer>
					</TabPanel>

					{/* <TextField
						value={overview_data}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							if (section_to_edit !== "property-overview") {
								dispatch(setSectionToEditThunk("property-overview"));
							}
							setIsButtonDisabled(false);
							dispatch(setSinglePropertyOverviewThunk(event.target.value));
						}}
						multiline
						rows={6}
						// label="Overview"
						placeholder="Eg: RERA Registered, BBMP Approved, IGBC Certified, Vaastu Compliance etc."
						fullWidth
						autoComplete="off"
					/> */}
				</div>
			)}
		</PaperContainer>
	);
};

export default OverviewCard;
